/**
 * RUBYTILL PAY — the "Live" block in the device panel: what the CPad is showing
 * now, its last events (screen / Tapro steps), and four Fleet commands.
 * Fleet deviceId for a RubyTill Pay device is `${tenantId}#${emvDeviceId}`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Settings2, FileText, ClipboardList, type LucideIcon } from 'lucide-react';
import type { FleetActionId } from '@platform/shared-types';
import { fleetSupportApi, type RtpayLiveState, type RtpayRefundLock } from '../../services/fleetSupportApi';
import { useDeviceCommands } from '../../pages/pos/fleet/useDeviceCommands';
import { toastService } from '../../services/toastService';
import { RubyGemMark } from '../RubyGemMark';

export const RTPAY_DEVICE_TYPE = 'dejavoo_rubytill_pay';
export const rtpayFleetDeviceId = (tenantId: string, emvDeviceId: string) => `${tenantId}#${emvDeviceId}`;

const ACTIONS: { id: FleetActionId; label: string; Icon: LucideIcon }[] = [
  { id: 'reload_page', label: 'Reload page', Icon: RefreshCw },
  { id: 'refresh_config', label: 'Refresh settings', Icon: Settings2 },
  { id: 'collect_logs', label: 'Collect logs', Icon: FileText },
  { id: 'device_report', label: 'Device report', Icon: ClipboardList },
];

const clock = (iso?: string | null) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
};

const stamp = (iso?: string | null) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? new Date(t).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
};
const KIND_LABEL: Record<string, string> = { refund: 'Refund', void: 'Void', unreferenced: 'No-receipt refund' };

/** Task 22 — what the stuck-lock sweep did with this CPad's refund / void locks. */
export function refundLockLine(l: RtpayRefundLock): { label: string; detail: string; attention: boolean } {
  const what = `${KIND_LABEL[l.kind] || 'Refund'}${typeof l.amount === 'number' ? ` $${l.amount.toFixed(2)}` : ''}`;
  if (l.mode === 'late') return { label: `${what} · refunded late — needs review`, detail: `SUNBAY approved it after the auto-release · ${stamp(l.lateRecordedAt)}`, attention: true };
  if (l.mode === 'watch') {
    const why = l.releaseReason ? ` (${l.releaseReason.replace(/^device_/, 'CPad ').replace(/_/g, ' ')})` : '';
    return { label: `${what} · auto-released, nothing refunded`, detail: `SUNBAY never recorded it${why} · ${stamp(l.releasedAt)} · watched 7 days`, attention: true };
  }
  return { label: `${what} · waiting for the CPad`, detail: `Started ${stamp(l.createdAt)}`, attention: false };
}

export const RubyTillPayLiveBlock: React.FC<{ tenantId: string; emvDeviceId: string; disabled?: boolean }> = ({ tenantId, emvDeviceId, disabled }) => {
  const fleetId = rtpayFleetDeviceId(tenantId, emvDeviceId);
  const [live, setLive] = useState<RtpayLiveState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const { commands, issue } = useDeviceCommands(fleetId, RTPAY_DEVICE_TYPE);

  const load = useCallback(async () => {
    try { setLive(await fleetSupportApi.getRtpayLiveState(tenantId, emvDeviceId)); setError(null); }
    catch (e: any) { setError(e?.message || 'Could not read the live state.'); }
  }, [tenantId, emvDeviceId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const run = async (action: FleetActionId, label: string) => {
    setBusy(action);
    try {
      const c = await issue(action);
      toastService.success(c.state === 'sent' ? `${label} sent.` : `${label} queued — the terminal is offline.`);
    } catch (e: any) {
      toastService.error(e?.message || `Could not send ${label.toLowerCase()}.`);
    } finally { setBusy(null); }
  };

  const openLog = async (commandId: string) => {
    try {
      const { url } = await fleetSupportApi.getArtifact(commandId, fleetId, 'rtpay-log.txt');
      window.open(url, '_blank', 'noopener');
    } catch (e: any) { toastService.error(e?.message || 'The log is not available.'); }
  };

  const events = [...(live?.recentEvents || [])].reverse().slice(0, 12);
  const locks = (live?.refundLocks || []).slice(0, 10);
  const recentCmds = commands.slice(0, 3);

  return (
    <div data-testid="rtpay-live" className="mt-3 rounded-xl border border-stone-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[13px] font-semibold text-stone-900">Live</div>
        <div className="text-xs text-stone-600" data-testid="rtpay-live-screen">
          {live?.lastScreen ? <>Showing <span className="font-medium text-stone-900">{live.lastScreen}</span>{live.lastScreenAt ? ` · ${clock(live.lastScreenAt)}` : ''}</> : live ? 'No screen reported yet' : error ? '' : <span className="inline-flex items-center gap-1.5"><RubyGemMark size={12} drawing color="currentColor" /></span>}
        </div>
      </div>
      {error && <p className="mt-1 text-xs text-amber-800">{error}</p>}

      <div className="mt-2 flex flex-wrap gap-2">
        {ACTIONS.map(({ id, label, Icon }) => (
          <button key={id} type="button" data-testid={`rtpay-live-${id}`} disabled={disabled || busy !== null}
            onClick={() => run(id, label)}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 text-[12px] font-semibold text-stone-700 hover:border-ruby hover:text-ruby disabled:opacity-50">
            {busy === id ? <RubyGemMark size={13} drawing color="currentColor" /> : <Icon size={13} />} {label}
          </button>
        ))}
      </div>

      {recentCmds.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-stone-600" data-testid="rtpay-live-commands">
          {recentCmds.map((c) => (
            <li key={c.commandId} className="flex flex-wrap items-center gap-x-2">
              <span className="font-medium text-stone-800">{c.action.replace(/_/g, ' ')}</span>
              <span>{c.state}</span>
              {c.action === 'collect_logs' && c.state === 'done' && (
                <button type="button" onClick={() => openLog(c.commandId)} className="min-h-[44px] font-semibold text-ruby hover:text-ruby-deep">View log</button>
              )}
            </li>
          ))}
        </ul>
      )}

      {locks.length > 0 && (
        <div className="mt-2" data-testid="rtpay-live-refund-locks">
          <div className="text-[12px] font-semibold text-stone-800">Refund locks</div>
          <ul className="mt-1 space-y-1 text-xs">
            {locks.map((l) => {
              const line = refundLockLine(l);
              return (
                <li key={l.refundRef} data-testid={`rtpay-live-lock-${l.mode}`} className="min-w-0">
                  <span className={line.attention ? 'font-semibold text-amber-800' : 'font-medium text-stone-800'}>{line.label}</span>
                  <span className="block truncate text-stone-500">{line.detail} · {l.refundRef}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {events.length > 0 && (
        <ul className="mt-2 max-h-48 divide-y divide-stone-100 overflow-y-auto text-xs" data-testid="rtpay-live-events">
          {events.map((e, i) => (
            <li key={`${e.at}-${i}`} className="flex gap-2 py-1">
              <span className="w-16 shrink-0 text-stone-400">{clock(e.at)}</span>
              <span className="font-medium text-stone-800">{e.kind}</span>
              <span className="min-w-0 truncate text-stone-600">{[e.screen, e.detail].filter(Boolean).join(' · ')}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RubyTillPayLiveBlock;
